import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
    ReactFlowProvider,
    Background,
    Controls,
    Handle,
    Position,
    useNodesState,
    useEdgesState,
    useReactFlow,
    ConnectionMode,
    Panel,
    MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import NearMeIcon from '@mui/icons-material/NearMe';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { THEME } from '../../theme';
import LogisticsFlowEdge from './LogisticsFlowEdge';
import './LogisticsNetworkCanvas.css';

const ROLE_COLORS = {
    plant: '#2cbb5d',
    dc: '#3b82f6',
    facility: '#22d3ee',
    depot: '#f59e0b',
    customer: '#ef4444',
};

const ROUTE_COLORS = ['#2cbb5d', '#3b82f6', '#f59e0b', '#22d3ee', '#a1a1aa'];

const NODE_CENTER = { x: 44, y: 28 };

const handleBase = {
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: '2px solid #0a0a0a',
    background: THEME.primary,
};

function LogisticsNode({ data, selected }) {
    const color = ROLE_COLORS[data.role] || THEME.primary;
    const open = Boolean(data.highlighted);
    const connectable = data?.connectable === true;
    const handleStyle = {
        ...handleBase,
        opacity: connectable ? 1 : 0,
        pointerEvents: connectable ? 'auto' : 'none',
    };

    return (
        <Box
            sx={{
                px: 1.5,
                py: 1,
                minWidth: 88,
                textAlign: 'center',
                bgcolor: open ? 'rgba(34, 211, 238, 0.12)' : '#121214',
                border: `2px solid ${selected ? THEME.primary : color}`,
                borderRadius: 1.5,
                boxShadow: open ? `0 0 16px ${color}` : 'none',
                animation: open ? 'facilityPulse 1.6s ease-in-out infinite' : 'none',
                cursor: connectable ? 'crosshair' : 'grab',
                '@keyframes facilityPulse': {
                    '0%, 100%': { boxShadow: `0 0 8px ${color}` },
                    '50%': { boxShadow: `0 0 22px ${color}` },
                },
            }}
        >
            <Typography variant="caption" sx={{ color, display: 'block', fontWeight: 700 }}>
                {data.role}
                {open ? ' · open' : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: THEME.textMain, fontWeight: 600 }}>
                {data.label}
            </Typography>

            {/* Handles required for React Flow edge creation (mirrors MarkovNode) */}
            <Handle id="t-top" type="target" position={Position.Top} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-top" type="source" position={Position.Top} style={{ ...handleStyle, left: '30%' }} isConnectable={connectable} />
            <Handle id="t-bottom" type="target" position={Position.Bottom} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-bottom" type="source" position={Position.Bottom} style={{ ...handleStyle, left: '70%' }} isConnectable={connectable} />
            <Handle id="t-left" type="target" position={Position.Left} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-left" type="source" position={Position.Left} style={{ ...handleStyle, top: '30%' }} isConnectable={connectable} />
            <Handle id="t-right" type="target" position={Position.Right} style={handleStyle} isConnectable={connectable} />
            <Handle id="s-right" type="source" position={Position.Right} style={{ ...handleStyle, top: '70%' }} isConnectable={connectable} />
        </Box>
    );
}

const nodeTypes = { logistics: LogisticsNode };
const edgeTypes = { logistics: LogisticsFlowEdge };

function edgeSpec(id, source, target, weight) {
    return {
        id,
        source,
        target,
        type: 'logistics',
        label: String(weight),
        data: { weight: String(weight) },
    };
}

const PRESETS = {
    transshipment: {
        nodes: [
            { id: 'p1', type: 'logistics', position: { x: 40, y: 80 }, data: { label: 'Plant A', role: 'plant', supply: 120 } },
            { id: 'p2', type: 'logistics', position: { x: 40, y: 240 }, data: { label: 'Plant B', role: 'plant', supply: 80 } },
            { id: 'd1', type: 'logistics', position: { x: 280, y: 80 }, data: { label: 'DC 1', role: 'dc' } },
            { id: 'd2', type: 'logistics', position: { x: 280, y: 240 }, data: { label: 'DC 2', role: 'dc' } },
            { id: 'c1', type: 'logistics', position: { x: 520, y: 40 }, data: { label: 'Cust 1', role: 'customer', demand: 50 } },
            { id: 'c2', type: 'logistics', position: { x: 520, y: 160 }, data: { label: 'Cust 2', role: 'customer', demand: 70 } },
            { id: 'c3', type: 'logistics', position: { x: 520, y: 280 }, data: { label: 'Cust 3', role: 'customer', demand: 40 } },
        ],
        edges: [
            edgeSpec('p1-d1', 'p1', 'd1', 2),
            edgeSpec('p1-d2', 'p1', 'd2', 3),
            edgeSpec('p2-d1', 'p2', 'd1', 4),
            edgeSpec('p2-d2', 'p2', 'd2', 1),
            edgeSpec('d1-c1', 'd1', 'c1', 2),
            edgeSpec('d1-c2', 'd1', 'c2', 3),
            edgeSpec('d1-c3', 'd1', 'c3', 5),
            edgeSpec('d2-c1', 'd2', 'c1', 4),
            edgeSpec('d2-c2', 'd2', 'c2', 2),
            edgeSpec('d2-c3', 'd2', 'c3', 1),
        ],
    },
    facility_location: {
        nodes: [
            { id: 'f1', type: 'logistics', position: { x: 80, y: 60 }, data: { label: 'Site 1', role: 'facility', fixed_cost: 100, capacity: 120 } },
            { id: 'f2', type: 'logistics', position: { x: 80, y: 200 }, data: { label: 'Site 2', role: 'facility', fixed_cost: 140, capacity: 150 } },
            { id: 'f3', type: 'logistics', position: { x: 80, y: 340 }, data: { label: 'Site 3', role: 'facility', fixed_cost: 90, capacity: 90 } },
            { id: 'c1', type: 'logistics', position: { x: 420, y: 40 }, data: { label: 'Cust 1', role: 'customer', demand: 40 } },
            { id: 'c2', type: 'logistics', position: { x: 420, y: 160 }, data: { label: 'Cust 2', role: 'customer', demand: 55 } },
            { id: 'c3', type: 'logistics', position: { x: 420, y: 280 }, data: { label: 'Cust 3', role: 'customer', demand: 35 } },
            { id: 'c4', type: 'logistics', position: { x: 420, y: 400 }, data: { label: 'Cust 4', role: 'customer', demand: 50 } },
        ],
        edges: [
            edgeSpec('f1-c1', 'f1', 'c1', 2),
            edgeSpec('f1-c2', 'f1', 'c2', 3),
            edgeSpec('f1-c3', 'f1', 'c3', 5),
            edgeSpec('f1-c4', 'f1', 'c4', 7),
            edgeSpec('f2-c1', 'f2', 'c1', 4),
            edgeSpec('f2-c2', 'f2', 'c2', 2),
            edgeSpec('f2-c3', 'f2', 'c3', 3),
            edgeSpec('f2-c4', 'f2', 'c4', 5),
            edgeSpec('f3-c1', 'f3', 'c1', 6),
            edgeSpec('f3-c2', 'f3', 'c2', 4),
            edgeSpec('f3-c3', 'f3', 'c3', 2),
            edgeSpec('f3-c4', 'f3', 'c4', 2),
        ],
    },
    cvrp: {
        nodes: [
            { id: 'depot', type: 'logistics', position: { x: 260, y: 200 }, data: { label: 'Depot', role: 'depot', capacity: 50 } },
            { id: 'c1', type: 'logistics', position: { x: 80, y: 60 }, data: { label: 'C1', role: 'customer', demand: 12 } },
            { id: 'c2', type: 'logistics', position: { x: 420, y: 40 }, data: { label: 'C2', role: 'customer', demand: 10 } },
            { id: 'c3', type: 'logistics', position: { x: 480, y: 220 }, data: { label: 'C3', role: 'customer', demand: 15 } },
            { id: 'c4', type: 'logistics', position: { x: 360, y: 360 }, data: { label: 'C4', role: 'customer', demand: 8 } },
            { id: 'c5', type: 'logistics', position: { x: 120, y: 340 }, data: { label: 'C5', role: 'customer', demand: 14 } },
        ],
        edges: [
            edgeSpec('depot-c1', 'depot', 'c1', 5),
            edgeSpec('depot-c2', 'depot', 'c2', 5),
            edgeSpec('depot-c3', 'depot', 'c3', 5),
            edgeSpec('depot-c4', 'depot', 'c4', 5),
            edgeSpec('depot-c5', 'depot', 'c5', 5),
            edgeSpec('c1-depot', 'c1', 'depot', 5),
            edgeSpec('c2-depot', 'c2', 'depot', 5),
            edgeSpec('c3-depot', 'c3', 'depot', 5),
            edgeSpec('c4-depot', 'c4', 'depot', 5),
            edgeSpec('c5-depot', 'c5', 'depot', 5),
            edgeSpec('c1-c2', 'c1', 'c2', 4),
            edgeSpec('c2-c3', 'c2', 'c3', 3),
            edgeSpec('c3-c4', 'c3', 'c4', 3),
            edgeSpec('c4-c5', 'c4', 'c5', 4),
            edgeSpec('c5-c1', 'c5', 'c1', 3),
            edgeSpec('c1-c5', 'c1', 'c5', 3),
            edgeSpec('c2-c1', 'c2', 'c1', 4),
            edgeSpec('c3-c2', 'c3', 'c2', 3),
            edgeSpec('c4-c3', 'c4', 'c3', 3),
            edgeSpec('c5-c4', 'c5', 'c4', 4),
            edgeSpec('c1-c3', 'c1', 'c3', 6),
            edgeSpec('c3-c1', 'c3', 'c1', 6),
            edgeSpec('c2-c4', 'c2', 'c4', 5),
            edgeSpec('c4-c2', 'c4', 'c2', 5),
            edgeSpec('c2-c5', 'c2', 'c5', 6),
            edgeSpec('c5-c2', 'c5', 'c2', 6),
            edgeSpec('c3-c5', 'c3', 'c5', 5),
            edgeSpec('c5-c3', 'c5', 'c3', 5),
            edgeSpec('c1-c4', 'c1', 'c4', 6),
            edgeSpec('c4-c1', 'c4', 'c1', 6),
        ],
    },
};

function ensureOptimalEdges(edges, optimalEdgeIds = []) {
    const existing = new Set(
        (edges || []).flatMap((e) => [`${e.source}->${e.target}`, e.id])
    );
    const next = [...(edges || [])];
    for (const key of optimalEdgeIds || []) {
        if (existing.has(key)) continue;
        const parts = String(key).split('->');
        if (parts.length !== 2) continue;
        const [source, target] = parts;
        const id = `opt-${source}-${target}`;
        next.push(edgeSpec(id, source, target, '·'));
        existing.add(key);
        existing.add(id);
    }
    return next;
}

/** Map source->target keys to shipment / allocation quantities. */
function buildFlowQtyMap({ optimalEdgeIds = [], shipments = [], allocations = [], nodes = [] }) {
    const map = {};
    const labelToId = {};
    for (const n of nodes || []) {
        labelToId[n.data?.label] = n.id;
        labelToId[n.id] = n.id;
    }

    for (const s of shipments || []) {
        const a = labelToId[s.from] || s.from;
        const b = labelToId[s.to] || s.to;
        if (a && b) {
            const key = `${a}->${b}`;
            map[key] = (map[key] || 0) + Number(s.qty || 0);
        }
    }

    for (const a of allocations || []) {
        const src = labelToId[a.facility] || a.facility;
        const tgt = labelToId[a.customer] || a.customer;
        if (src && tgt) {
            const key = `${src}->${tgt}`;
            map[key] = (map[key] || 0) + Number(a.qty ?? a.fraction ?? 0);
        }
    }

    // Fallback: mark optimal edges present even without qty
    for (const key of optimalEdgeIds || []) {
        if (map[key] == null) map[key] = 1;
    }
    return map;
}

/** Resolve CVRP route edge keys + vehicle color index. */
function buildRouteEdgeMeta(routes, nodes, optimalEdgeIds) {
    const labelToId = {};
    for (const n of nodes || []) {
        labelToId[n.data?.label] = n.id;
        labelToId[n.id] = n.id;
    }

    const edgeMeta = {}; // key -> { vehicleIdx, order, color }
    const orderedKeys = [];

    if (routes?.length) {
        routes.forEach((route, ri) => {
            const ids = (route.path || [])
                .map((lab) => labelToId[lab] || labelToId[String(lab)])
                .filter(Boolean);
            for (let i = 0; i < ids.length - 1; i++) {
                const key = `${ids[i]}->${ids[i + 1]}`;
                if (!edgeMeta[key]) {
                    edgeMeta[key] = {
                        vehicleIdx: ri,
                        order: orderedKeys.length,
                        color: ROUTE_COLORS[ri % ROUTE_COLORS.length],
                    };
                    orderedKeys.push(key);
                }
            }
        });
    } else {
        (optimalEdgeIds || []).forEach((key, i) => {
            edgeMeta[key] = {
                vehicleIdx: 0,
                order: i,
                color: ROUTE_COLORS[0],
            };
            orderedKeys.push(key);
        });
    }

    return { edgeMeta, orderedKeys };
}

function CanvasInner({
    problem,
    onGraphChange,
    optimalEdgeIds,
    optimalNodeIds,
    routes,
    shipments,
    allocations,
}) {
    const { screenToFlowPosition } = useReactFlow();
    const roles = useMemo(() => {
        if (problem === 'transshipment') return ['plant', 'dc', 'customer'];
        if (problem === 'facility_location') return ['facility', 'customer'];
        if (problem === 'cvrp') return ['depot', 'customer'];
        return ['customer'];
    }, [problem]);

    const preset = PRESETS[problem] || PRESETS.transshipment;
    const [nodes, setNodes, onNodesChange] = useNodesState(preset.nodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(preset.edges);
    const [tool, setTool] = useState('select');
    const [dialog, setDialog] = useState(null);
    const [pathStep, setPathStep] = useState(0);
    const [packetPhase, setPacketPhase] = useState(0);
    const [selectedEdgeIds, setSelectedEdgeIds] = useState(() => new Set());
    const animStart = useRef(performance.now());

    const edgeMode = tool === 'edge';

    const openEdgeEdit = useCallback((edge) => {
        if (!edge) return;
        setDialog({
            type: 'edge',
            editId: edge.id,
            source: edge.source,
            target: edge.target,
            weight: String(edge.data?.weight ?? edge.label ?? '1'),
        });
    }, []);

    useEffect(() => {
        const next = PRESETS[problem] || PRESETS.transshipment;
        setNodes(next.nodes);
        setEdges(next.edges);
        setTool('select');
        setPathStep(0);
        setSelectedEdgeIds(new Set());
        setDialog(null);
    }, [problem, setNodes, setEdges]);

    // Ensure solver-required edges exist on the canvas
    useEffect(() => {
        if (!optimalEdgeIds?.length) return;
        setEdges((eds) => ensureOptimalEdges(eds, optimalEdgeIds));
    }, [optimalEdgeIds, setEdges]);

    // Packet / CVRP step clock
    const hasSolution = Boolean(
        (optimalEdgeIds && optimalEdgeIds.length) ||
            (routes && routes.length) ||
            (shipments && shipments.length) ||
            (allocations && allocations.length)
    );

    const routeMeta = useMemo(
        () => buildRouteEdgeMeta(routes, nodes, optimalEdgeIds),
        [routes, nodes, optimalEdgeIds]
    );

    const pathEdgeCount = problem === 'cvrp' ? routeMeta.orderedKeys.length : 0;

    useEffect(() => {
        if (!hasSolution) return undefined;
        animStart.current = performance.now();
        let frame;
        const loop = (now) => {
            const elapsed = now - animStart.current;
            setPacketPhase((elapsed % 2400) / 2400);
            if (problem === 'cvrp' && pathEdgeCount > 0) {
                const step = Math.floor(elapsed / 900) % (pathEdgeCount + 1);
                setPathStep(step);
            }
            frame = requestAnimationFrame(loop);
        };
        frame = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frame);
    }, [hasSolution, problem, pathEdgeCount, optimalEdgeIds, routes]);

    const flowQtyMap = useMemo(
        () =>
            buildFlowQtyMap({
                optimalEdgeIds,
                shipments,
                allocations,
                nodes,
            }),
        [optimalEdgeIds, shipments, allocations, nodes]
    );

    const solutionMaps = useMemo(() => {
        const edgeState = {};
        const edgeFlow = {};
        const edgeRouteColor = {};
        const activeKeys = new Set(optimalEdgeIds || []);

        if (problem === 'cvrp' && routeMeta.orderedKeys.length) {
            routeMeta.orderedKeys.forEach((key) => {
                edgeState[key] = 'dim';
            });
            // Dim non-route edges when we have a solution
            (edges || []).forEach((e) => {
                const key = `${e.source}->${e.target}`;
                if (!routeMeta.edgeMeta[key]) edgeState[key] = 'dim';
            });
            routeMeta.orderedKeys.forEach((key, i) => {
                const meta = routeMeta.edgeMeta[key];
                edgeRouteColor[key] = meta.color;
                if (i < pathStep) edgeState[key] = 'done';
                else if (i === pathStep) edgeState[key] = 'active';
                else edgeState[key] = 'upcoming';
            });
        } else if (hasSolution) {
            (edges || []).forEach((e) => {
                const key = `${e.source}->${e.target}`;
                if (activeKeys.has(key) || activeKeys.has(e.id) || flowQtyMap[key]) {
                    edgeState[key] = 'flow';
                    edgeState[e.id] = 'flow';
                    edgeFlow[key] = flowQtyMap[key] || 1;
                    edgeFlow[e.id] = flowQtyMap[key] || 1;
                } else if (optimalEdgeIds?.length) {
                    edgeState[key] = 'dim';
                    edgeState[e.id] = 'dim';
                }
            });
            // Keys not yet on canvas
            activeKeys.forEach((key) => {
                if (!edgeState[key]) edgeState[key] = 'flow';
                if (flowQtyMap[key] != null) edgeFlow[key] = flowQtyMap[key];
            });
        }

        return { edgeState, edgeFlow, edgeRouteColor };
    }, [
        problem,
        hasSolution,
        optimalEdgeIds,
        flowQtyMap,
        routeMeta,
        pathStep,
        edges,
    ]);

    const decoratedNodes = useMemo(
        () =>
            nodes.map((n) => ({
                ...n,
                type: 'logistics',
                draggable: tool === 'select',
                connectable: edgeMode,
                data: {
                    ...n.data,
                    connectable: edgeMode,
                    highlighted: (optimalNodeIds || []).includes(n.id),
                },
            })),
        [nodes, tool, edgeMode, optimalNodeIds]
    );

    const decoratedEdges = useMemo(
        () =>
            edges.map((e) => {
                const key = `${e.source}->${e.target}`;
                const state =
                    solutionMaps.edgeState[e.id] ||
                    solutionMaps.edgeState[key] ||
                    'off';
                const qty = flowQtyMap[key] || solutionMaps.edgeFlow[key] || 0;
                const routeColor =
                    solutionMaps.edgeRouteColor[key] ||
                    (problem === 'cvrp' && state !== 'off' && state !== 'dim'
                        ? ROUTE_COLORS[0]
                        : undefined);
                const showPackets =
                    state === 'flow' || state === 'active' || (state === 'done' && problem !== 'cvrp');
                const selected = e.selected || selectedEdgeIds.has(e.id);

                return {
                    ...e,
                    selected,
                    type: 'logistics',
                    animated: false,
                    zIndex: state === 'active' || state === 'flow' ? 10 : 0,
                    markerEnd: undefined,
                    data: {
                        ...e.data,
                        solutionState: state,
                        flowQty: qty,
                        flowStrength: qty ? Math.min(1, qty / 80) : state === 'flow' ? 0.5 : 0,
                        routeColor,
                        showPackets,
                        packetPhase,
                        onEditWeight: () => openEdgeEdit(e),
                    },
                    label: e.label ?? e.data?.weight,
                    style: {},
                };
            }),
        [edges, solutionMaps, flowQtyMap, problem, packetPhase, selectedEdgeIds, openEdgeEdit]
    );

    useEffect(() => {
        onGraphChange?.({ nodes, edges });
    }, [nodes, edges, onGraphChange]);

    const onConnect = useCallback((connection) => {
        if (!connection?.source || !connection?.target) return;
        setDialog({
            type: 'edge',
            source: connection.source,
            target: connection.target,
            sourceHandle: connection.sourceHandle,
            targetHandle: connection.targetHandle,
            weight: '1',
        });
    }, []);

    const onPaneClick = useCallback(
        (event) => {
            if (tool !== 'add') return;
            const pos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            setDialog({
                type: 'node',
                label: `N${nodes.length + 1}`,
                role: roles[0],
                supply: 100,
                demand: 20,
                capacity: 100,
                fixed_cost: 100,
                x: pos.x - NODE_CENTER.x,
                y: pos.y - NODE_CENTER.y,
            });
        },
        [tool, nodes.length, roles, screenToFlowPosition]
    );

    const confirmDialog = () => {
        if (!dialog) return;
        if (dialog.type === 'edge') {
            const w = String(dialog.weight ?? '').trim() || '1';
            if (dialog.editId) {
                setEdges((eds) =>
                    eds.map((e) =>
                        e.id === dialog.editId
                            ? { ...e, label: w, data: { ...e.data, weight: w } }
                            : e
                    )
                );
            } else {
                const id = `${dialog.source}-${dialog.target}-${Date.now()}`;
                setEdges((eds) => [
                    ...eds,
                    {
                        id,
                        source: dialog.source,
                        target: dialog.target,
                        sourceHandle: dialog.sourceHandle || undefined,
                        targetHandle: dialog.targetHandle || undefined,
                        type: 'logistics',
                        label: w,
                        data: { weight: w },
                    },
                ]);
            }
        } else {
            const id = `n${Date.now()}`;
            const data = {
                label: dialog.label,
                role: dialog.role,
            };
            if (dialog.role === 'plant') data.supply = Number(dialog.supply) || 0;
            if (dialog.role === 'customer') data.demand = Number(dialog.demand) || 0;
            if (dialog.role === 'facility') {
                data.capacity = Number(dialog.capacity) || 0;
                data.fixed_cost = Number(dialog.fixed_cost) || 0;
            }
            if (dialog.role === 'depot') data.capacity = Number(dialog.capacity) || 0;
            setNodes((nds) => [
                ...nds,
                {
                    id,
                    type: 'logistics',
                    position: { x: dialog.x, y: dialog.y },
                    data,
                },
            ]);
        }
        setDialog(null);
        setTool('select');
    };

    const onEdgeDoubleClick = useCallback(
        (_ev, edge) => {
            openEdgeEdit(edge);
        },
        [openEdgeEdit]
    );

    const onEdgeClick = useCallback(
        (_ev, edge) => {
            setSelectedEdgeIds(new Set([edge.id]));
            setEdges((eds) =>
                eds.map((e) => ({
                    ...e,
                    selected: e.id === edge.id,
                }))
            );
            setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
        },
        [setEdges, setNodes]
    );

    const onNodeClick = useCallback(
        (_ev, node) => {
            setSelectedEdgeIds(new Set());
            setEdges((eds) => eds.map((e) => ({ ...e, selected: false })));
            setNodes((nds) =>
                nds.map((n) => ({
                    ...n,
                    selected: n.id === node.id,
                }))
            );
        },
        [setEdges, setNodes]
    );

    const deleteSelected = useCallback(() => {
        setEdges((eds) => {
            const drop = new Set(
                eds.filter((e) => e.selected || selectedEdgeIds.has(e.id)).map((e) => e.id)
            );
            return eds.filter((e) => !drop.has(e.id));
        });
        setNodes((nds) => {
            const removed = new Set(nds.filter((n) => n.selected).map((n) => n.id));
            if (removed.size) {
                setEdges((eds) =>
                    eds.filter((e) => !removed.has(e.source) && !removed.has(e.target))
                );
            }
            return nds.filter((n) => !n.selected);
        });
        setSelectedEdgeIds(new Set());
    }, [setNodes, setEdges, selectedEdgeIds]);

    useEffect(() => {
        const onKey = (e) => {
            if (document.activeElement?.tagName === 'INPUT') return;
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                deleteSelected();
                return;
            }
            if (e.key === 'Enter') {
                const edge = edges.find((ed) => ed.selected || selectedEdgeIds.has(ed.id));
                if (edge) {
                    e.preventDefault();
                    openEdgeEdit(edge);
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [deleteSelected, edges, selectedEdgeIds, openEdgeEdit]);

    const toolBtn = (active) =>
        active
            ? { bgcolor: `${THEME.primary} !important`, color: '#000 !important' }
            : {};

    return (
        <Box
            sx={{
                height: 480,
                border: `1px solid ${THEME.border}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#0a0a0a',
                position: 'relative',
            }}
        >
            <ReactFlow
                nodes={decoratedNodes}
                edges={decoratedEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onPaneClick={(ev) => {
                    setSelectedEdgeIds(new Set());
                    onPaneClick(ev);
                }}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onEdgeDoubleClick={onEdgeDoubleClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodesConnectable={edgeMode}
                elementsSelectable
                edgesFocusable
                deleteKeyCode={null}
                connectionMode={ConnectionMode.Loose}
                connectionLineStyle={{
                    stroke: THEME.primary,
                    strokeWidth: 2.5,
                }}
                connectionLineType="bezier"
                panOnDrag={tool === 'select'}
                fitView
                minZoom={0.4}
                maxZoom={1.8}
                defaultEdgeOptions={{
                    type: 'logistics',
                    selectable: true,
                    focusable: true,
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#555' },
                }}
                proOptions={{ hideAttribution: true }}
            >
                <Background color="#222" gap={18} />
                <Controls />
                <Panel position="top-left">
                    <ButtonGroup
                        size="small"
                        sx={{
                            bgcolor: '#18181b',
                            border: `1px solid ${THEME.border}`,
                            '& .MuiButton-root': {
                                color: THEME.secondary,
                                textTransform: 'none',
                                borderColor: THEME.border,
                            },
                        }}
                    >
                        <Tooltip title="Select and drag nodes">
                            <Button
                                onClick={() => setTool('select')}
                                variant={tool === 'select' ? 'contained' : 'outlined'}
                                sx={toolBtn(tool === 'select')}
                            >
                                <NearMeIcon fontSize="small" sx={{ mr: 0.5 }} />
                                Select
                            </Button>
                        </Tooltip>
                        <Tooltip title="Click canvas to place a node">
                            <Button
                                onClick={() => setTool('add')}
                                variant={tool === 'add' ? 'contained' : 'outlined'}
                                sx={toolBtn(tool === 'add')}
                                startIcon={<AddCircleOutlineIcon />}
                            >
                                Node
                            </Button>
                        </Tooltip>
                        <Tooltip title="Drag from a green handle to another node">
                            <Button
                                onClick={() => setTool('edge')}
                                variant={tool === 'edge' ? 'contained' : 'outlined'}
                                sx={toolBtn(tool === 'edge')}
                                startIcon={<AltRouteIcon />}
                            >
                                Connect
                            </Button>
                        </Tooltip>
                        <Button
                            startIcon={<DeleteOutlineIcon />}
                            onClick={deleteSelected}
                            sx={{ color: THEME.accent, borderColor: THEME.border }}
                        >
                            Delete
                        </Button>
                    </ButtonGroup>
                </Panel>
                <Panel position="bottom-center">
                    <Typography
                        variant="caption"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            bgcolor: 'rgba(24,24,27,0.9)',
                            border: `1px solid ${THEME.border}`,
                            borderRadius: 1,
                            color: THEME.secondary,
                        }}
                    >
                        {tool === 'add' && 'Click empty space to add a node'}
                        {tool === 'edge' &&
                            'Drag from a green handle on a node to another node to create an edge'}
                        {tool === 'select' &&
                            (hasSolution
                                ? 'Solution animating · Double-click edge/cost to edit · Delete removes'
                                : 'Double-click edge (or cost) to edit · Enter edits selected · Connect to draw')}
                    </Typography>
                </Panel>
            </ReactFlow>

            <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ bgcolor: THEME.cardBg, color: THEME.textMain }}>
                    {dialog?.type === 'edge'
                        ? dialog.editId
                            ? 'Edit edge cost'
                            : 'Edge cost'
                        : 'Add node'}
                </DialogTitle>
                <DialogContent sx={{ bgcolor: THEME.cardBg, pt: 2 }}>
                    {dialog?.type === 'edge' ? (
                        <TextField
                            fullWidth
                            size="small"
                            label="Cost / weight"
                            value={dialog.weight}
                            onChange={(e) => setDialog({ ...dialog, weight: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && confirmDialog()}
                            sx={{ mt: 1, input: { color: THEME.textMain } }}
                            autoFocus
                        />
                    ) : dialog ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                            <TextField
                                size="small"
                                label="Label"
                                value={dialog.label}
                                onChange={(e) => setDialog({ ...dialog, label: e.target.value })}
                                InputProps={{ sx: { color: THEME.textMain } }}
                            />
                            <TextField
                                select
                                size="small"
                                label="Role"
                                value={dialog.role}
                                onChange={(e) => setDialog({ ...dialog, role: e.target.value })}
                                InputProps={{ sx: { color: THEME.textMain } }}
                            >
                                {roles.map((r) => (
                                    <MenuItem key={r} value={r}>
                                        {r}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {dialog.role === 'plant' && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Supply"
                                    value={dialog.supply}
                                    onChange={(e) => setDialog({ ...dialog, supply: e.target.value })}
                                    InputProps={{ sx: { color: THEME.textMain } }}
                                />
                            )}
                            {dialog.role === 'customer' && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Demand"
                                    value={dialog.demand}
                                    onChange={(e) => setDialog({ ...dialog, demand: e.target.value })}
                                    InputProps={{ sx: { color: THEME.textMain } }}
                                />
                            )}
                            {(dialog.role === 'facility' || dialog.role === 'depot') && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Capacity"
                                    value={dialog.capacity}
                                    onChange={(e) => setDialog({ ...dialog, capacity: e.target.value })}
                                    InputProps={{ sx: { color: THEME.textMain } }}
                                />
                            )}
                            {dialog.role === 'facility' && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Fixed cost"
                                    value={dialog.fixed_cost}
                                    onChange={(e) => setDialog({ ...dialog, fixed_cost: e.target.value })}
                                    InputProps={{ sx: { color: THEME.textMain } }}
                                />
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ bgcolor: THEME.cardBg }}>
                    <Button onClick={() => setDialog(null)} sx={{ color: THEME.secondary }}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDialog} sx={{ color: THEME.primary }}>
                        {dialog?.type === 'edge' && dialog?.editId ? 'OK' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default function LogisticsNetworkCanvas(props) {
    return (
        <ReactFlowProvider>
            <CanvasInner {...props} />
        </ReactFlowProvider>
    );
}

export { PRESETS, buildFlowQtyMap, ensureOptimalEdges, buildRouteEdgeMeta };
